import { useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { CreateArticleDialog } from '@/components/admin/library/CreateArticleDialog';
import { CreatePDFDialog } from '@/components/admin/library/CreatePDFDialog';
import { MaterialCard } from '@/components/library/MaterialCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Book, FileText, RotateCw, Search, Library } from 'lucide-react';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { toast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AdminLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isArticleDialogOpen, setIsArticleDialogOpen] = useState(false);
  const [isPDFDialogOpen, setIsPDFDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<{ id: string; coverImage: string; fileUrl?: string } | null>(null);
  const { materials, isLoading, deleteMaterial, fetchMaterials } = useDigitalMaterials();

  const filteredMaterials = materials?.filter(material =>
    material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    material.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async () => {
    if (!materialToDelete) return;

    try {
      await deleteMaterial(materialToDelete.id);
      toast({
        title: 'Material eliminado',
        description: 'El material se ha eliminado correctamente de la base de datos.',
      });
      // Recargar la lista después de eliminar
      fetchMaterials();
    } catch (error) {
      console.error('Error deleting material:', error);
      toast({
        title: 'Error al eliminar',
        description: 'No se pudo eliminar el material. Por favor, intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setMaterialToDelete(null);
    }
  };

  const handleMaterialCreated = () => {
    // Recargar la lista después de crear un material
    fetchMaterials();
    setIsArticleDialogOpen(false);
    setIsPDFDialogOpen(false);
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-6">
        <div className="flex items-center mb-8">
          <div className="flex items-center gap-3">
            <Library className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Biblioteca Digital</h1>
          </div>
        </div>

        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => fetchMaterials()}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RotateCw className="h-4 w-4" />
            Recargar
          </Button>
          <Button 
            onClick={() => setIsArticleDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <Book className="h-4 w-4" />
            Nuevo Artículo
          </Button>
          <Button 
            onClick={() => setIsPDFDialogOpen(true)}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Subir PDF
          </Button>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
            <Input
              placeholder="Buscar materiales..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="border rounded-lg p-4 space-y-4">
                <div className="h-48 bg-gray-200 animate-pulse rounded-lg" />
                <div className="h-6 bg-gray-200 animate-pulse rounded w-3/4" />
                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMaterials?.map((material) => (
              <MaterialCard
                key={material.id}
                material={material}
                onDelete={() => setMaterialToDelete(material)}
                showAdminControls={true}
              />
            ))}
          </div>
        )}

        <CreateArticleDialog
          open={isArticleDialogOpen}
          onOpenChange={setIsArticleDialogOpen}
          onSuccess={handleMaterialCreated}
        />

        <CreatePDFDialog
          open={isPDFDialogOpen}
          onOpenChange={setIsPDFDialogOpen}
          onSuccess={handleMaterialCreated}
        />

        <AlertDialog open={!!materialToDelete} onOpenChange={() => setMaterialToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción eliminará el material de la base de datos. Los archivos en Cloudflare R2 deberán ser eliminados manualmente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLibrary;
